import cloneDeep from 'lodash/cloneDeep';

import type { MailParams } from '@webapp/account/src/resources/mailing/mailing';
import type { SmsParams } from '@webapp/account/src/resources/smsing';
import type { Addons } from '@webapp/account/src/resources/survey/addons/addons';
import type { Css } from '@webapp/account/src/resources/survey/addons/css';
import type { Popup } from '@webapp/account/src/resources/survey/addons/popup';
import type { Widget } from '@webapp/account/src/resources/survey/addons/widget';
import type { Chart, ChartData } from '@webapp/account/src/resources/survey/chart';
import type { Folder } from '@webapp/account/src/resources/survey/folder';
import { findFolder, SURVEY_LIST_PER_PAGE, withTests } from '@webapp/account/src/resources/survey/lib';

import type { ChartGrouping } from '@webapp/common/lib/date';
import { extractSubdomain } from '@webapp/common/lib/utils';
import type { SurveyAddonAlert } from '@webapp/common/resources/entities/survey-addons/alert';
import type { SurveyAddonBrand } from '@webapp/common/resources/entities/survey-addons/brand';
import type { SurveyAddonButtons } from '@webapp/common/resources/entities/survey-addons/buttons';
import type { SurveyAddonFonts } from '@webapp/common/resources/entities/survey-addons/fonts';
import type { SurveyAddonMain } from '@webapp/common/resources/entities/survey-addons/main';
import type { SurveyAddonParams } from '@webapp/common/resources/entities/survey-addons/params';
import type { SurveyAddonTests } from '@webapp/common/resources/entities/survey-addons/tests';
import type { SurveyAddonView } from '@webapp/common/resources/entities/survey-addons/view';
import type { QuestionType } from '@webapp/common/resources/survey';

import type { States } from '../states';

import type { Email, Notify } from './addons/notify';
import type { ShareLink, SurveyState } from './state';
import type { Survey } from './survey';
import { initialSurvey } from './survey';

const normalizeStr = (str: string): string => str.toLocaleLowerCase().trim().replace(/\s\s+/g, ' ');

export const filterVisibleItems = (items: Array<Survey>, search: string): Array<Survey> => {
    search = search && normalizeStr(search);

    let searchSubdomain = extractSubdomain(search);
    searchSubdomain = searchSubdomain || search;

    return search
        ? items.filter(
              ({ id, name, subdomain, title }) =>
                  (name && normalizeStr(name).includes(search)) ||
                  (title && normalizeStr(title).includes(search)) ||
                  String(id).includes(search) ||
                  (subdomain && subdomain.toLocaleLowerCase().includes(searchSubdomain)),
          )
        : items.filter(({ hidden }) => !hidden);
};

export const selectSurveys = ({ Survey }: States): SurveyState => Survey;

export const selectSurveySearch = (states: States): string => {
    const r = selectSurveys(states);

    return r.search;
};

export const selectSurveyPaginationPage = (states: States): number => {
    const r = selectSurveys(states);

    return r.paginationPage;
};

export const selectActiveSurveyId = (states: States): number => {
    const s = selectSurveys(states);

    return s.activeSurveyId;
};

export const selectIsActiveSurveyLoaded = (states: States): boolean => {
    const l = selectSurveyList(states);
    const id = selectActiveSurveyId(states);

    return Boolean(l.find((s) => s.id === id));
};

export const selectActiveSurvey = (states: States): Survey => {
    const l = selectSurveyList(states);
    const id = selectActiveSurveyId(states);

    return l.find((s) => s.id === id) || cloneDeep(initialSurvey);
};

export const selectActiveSurveyFolderId = (states: States): number => {
    const r = selectActiveSurvey(states);

    return r.folderId;
};

export const selectSurveyById =
    (surveyId: number) =>
    (states: States): Survey => {
        const r = selectActiveSurvey(states);

        if (r.id === surveyId) {
            return r;
        }

        const l = selectSurveyList(states);

        return l.find(({ id }) => id === surveyId);
    };

export const selectSurveyListFetch = (states: States): boolean => {
    const r = selectSurveys(states);

    return r.fetchItems;
};

export const selectSurveyParamsSave = (states: States): boolean => {
    const r = selectSurveys(states);

    return r.saveParams;
};

export const selectFileLink = (states: States): string => {
    const r = selectSurveys(states);

    return r.fileLink;
};

export const selectQuestionTypes = (states: States): Set<QuestionType> => {
    const r = selectActiveSurvey(states);

    return r.questionTypes;
};

export const selectWithTest = (states: States): boolean => {
    const qt = selectQuestionTypes(states);

    return withTests(qt);
};

export const selectCharts = (states: States): Array<Chart> => {
    const r = selectSurveys(states);

    return r.charts;
};

export const selectChartGrouping = (states: States): ChartGrouping => {
    const r = selectSurveys(states);

    return r.chartGrouping;
};

export const selectChartData = (states: States): ChartData => {
    const r = selectSurveys(states);

    return r.chartData;
};

export const selectAddons = (states: States): Addons => {
    const r = selectActiveSurvey(states);

    return r.addons;
};

export const selectWidget = (states: States): Widget => {
    const a = selectAddons(states);

    return a.widget;
};

export const selectPopup = (states: States): Popup => {
    const a = selectAddons(states);

    return a.popup;
};

export const selectButtons = (states: States): SurveyAddonButtons => {
    const a = selectAddons(states);

    return a.buttons;
};

export const selectParams = (states: States): SurveyAddonParams => {
    const a = selectAddons(states);

    return a.params;
};

export const selectAlert = (states: States): SurveyAddonAlert => {
    const a = selectAddons(states);

    return a.alert;
};

export const selectMain = (states: States): SurveyAddonMain => {
    const a = selectAddons(states);

    return a.main;
};

export const selectBrand = (states: States): SurveyAddonBrand => {
    const a = selectAddons(states);

    return a.brand;
};

export const selectFonts = (states: States): SurveyAddonFonts => {
    const a = selectAddons(states);

    return a.fonts;
};

export const selectView = (states: States): SurveyAddonView => {
    const a = selectAddons(states);

    return a.view;
};

export const selectCss = (states: States): Css => {
    const a = selectAddons(states);

    return a.css;
};

export const selectMailing = (states: States): MailParams => {
    const a = selectAddons(states);

    return a.mailing;
};

export const selectSmsing = (states: States): SmsParams => {
    const a = selectAddons(states);

    return a.smsing;
};

export const selectNotify = (states: States): Notify => {
    const a = selectAddons(states);
    return a.notify;
};

export const selectNotifyEmails = (states: States): Array<Email> => {
    const n = selectNotify(states);

    return n.emails;
};

export const selectSaveNotComplete = (states: States): boolean => {
    const a = selectMain(states);

    return a.savingNotComplete;
};

export const selectTests = (states: States): SurveyAddonTests => {
    const a = selectAddons(states);

    return a.tests;
};

export const selectMaxScoreAmount = (states: States): number => {
    const t = selectTests(states);

    return t.maxScoreAmount;
};

export const selectTotalFileSize = (states: States): number => {
    const r = selectActiveSurvey(states);

    return r.totalFileSize;
};

export const selectTotalFileCount = (states: States): number => {
    const r = selectActiveSurvey(states);

    return r.totalFileCount;
};

export const selectTotalAudioSize = (states: States): number => {
    const r = selectActiveSurvey(states);

    return r.totalAudioSize;
};

export const selectTotalAudioCount = (states: States): number => {
    const r = selectActiveSurvey(states);

    return r.totalAudioCount;
};

export const selectFoldersFetch = (states: States): boolean => {
    const r = selectSurveys(states);

    return r.fetchFolders;
};

export const selectSurveyList = (states: States): Array<Survey> => {
    const r = selectSurveys(states);

    return r.items;
};

export const selectVisibleSurveys = (states: States): Array<Survey> => {
    const r = selectSurveyList(states);
    const s = selectSurveySearch(states);

    return filterVisibleItems(r, s);
};

export const selectVisibleSurveysCount = (states: States): number => {
    const ss = selectVisibleSurveys(states);

    return ss.length;
};

export const selectVisibleSurveysPaginated = (states: States): Array<Survey> => {
    const p = selectSurveyPaginationPage(states);
    const ss = selectVisibleSurveys(states);
    const offset = (p - 1) * SURVEY_LIST_PER_PAGE;

    return ss.slice(offset, offset + SURVEY_LIST_PER_PAGE);
};

export const selectFolders = (states: States): Array<Folder> => {
    const r = selectSurveys(states);

    return r.folders;
};

export const selectAllFolders = (states: States): Array<Folder> => {
    const r = selectSurveys(states);

    return r.folders.reduce((acc, item) => acc.concat([item, item.children]), []).flat();
};

export const selectExpandedFolderId = (states: States): number => {
    const r = selectSurveys(states);

    return r.expandedFolderId;
};

export const selectFoldersByParent =
    (parentId: number) =>
    (states: States): Array<Folder> => {
        const f = selectFolders(states);

        if (parentId === null) {
            return f;
        }

        return f.find(({ id }) => id === parentId).children;
    };

export const selectFolderById =
    (folderId: number) =>
    (states: States): Folder => {
        const { folders } = selectSurveys(states);

        return findFolder(folders, folderId);
    };

export const selectCountWithoutFolder = (states: States): number => {
    const r = selectSurveys(states);

    return r.countWithoutFolder;
};

export const selectSurveyCount = (states: States): number => {
    const r = selectSurveyList(states);

    return r.length;
};

export const selectCurrentSurveyId = (states: States): number => {
    const i = selectActiveSurvey(states);

    return i.id;
};

export const selectActiveSurveyTitle = (states: States): string => {
    const i = selectActiveSurvey(states);

    return i.title;
};

export const selectSubdomain = (states: States): string => {
    const i = selectActiveSurvey(states);

    return i.subdomain;
};

export const selectMinFilterDate = (states: States): string => {
    const r = selectActiveSurvey(states);

    return r.createdDatetime.split(' ')[0];
};

export const selectShareLinks = (states: States): Array<ShareLink> => {
    const r = selectSurveys(states);

    return r.shareLinks;
};
